import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useTransition,
} from "react";
import PropTypes from "prop-types";
import "./Dashboard.css";
import NewNoteModal from "../Modals/NewNoteModal";
import EditNoteModal from "../Modals/EditNoteModal";
import AttachedFileModal from "../Modals/AttachedfileModal.jsx";
import ViewNoteModal from "../Modals/ViewNoteModal";
import toast from "react-hot-toast";
import InlineImageViewer from "../Modals/InlineImageViewer";
import NotesTab from "../NotesTab/NotesTab.jsx";
import DashboardMenu from "./DashboardMenu.jsx";
import DashboardHeader from "./DashboardHeader.jsx";
import NewTaskModal from "../Modals/NewTaskModal.jsx";
import UrlNoteModal from "../Modals/UrlNoteModal";
import DeleteConfirmationModal from "./shared/DeleteConfirmationModal.jsx";
import TableImageIcon from "./shared/TableImageIcon";
import CardImageIcon from "./shared/CardImageIcon";
import StackedImageIcon from "./shared/StackedImageIcon";

import { useFilters } from "./hooks/useFilters";
import { useSearch } from "./hooks/useSearch";
import { useStackedView } from "./hooks/useStackedView";
import { useNotesData } from "./hooks/useNotesData";
import { usePriority } from "./hooks/usePriority";
import { useInlineImages } from "./hooks/useInlineImages";
import { useUserWorkspace } from "./hooks/useUserWorkspace";
import { useDeleteNote } from "./hooks/useDeleteNote";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { useCurrentUser } from "./hooks/useCurrentUser";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { useActiveFiltersUI } from "./hooks/useActiveFiltersUI";
import { useModals } from "./hooks/useModals";
import { useStackedPersistentState } from "./hooks/useStackedPersistentState";
import { useMobileDetection } from "./hooks/useMobileDetection";
import { useClickOutside } from "./hooks/useClickOutside";

const MemoizedDashboardMenu = React.memo(DashboardMenu);
const MemoizedDashboardHeader = React.memo(DashboardHeader);
const MemoizedNotesTab = React.memo(NotesTab);

const getInitialUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sitenoteId = urlParams.get("SitenoteId");
  const taskId = urlParams.get("TaskId");
  if (sitenoteId && !isNaN(parseInt(sitenoteId)))
    return { type: "note", id: parseInt(sitenoteId) };
  if (taskId && !isNaN(parseInt(taskId)))
    return { type: "task", id: parseInt(taskId) };
  return null;
};

const initialUrlParams = getInitialUrlParams();
if (initialUrlParams)
  window.history.replaceState({}, "", window.location.pathname);

const Dashboard = ({
  notes,
  userid,
  userRole,
  refreshNotes,
  addSiteNote,
  updateNote,
  projects,
  jobs,
  tasks,
  onUploadDocument,
  onDeleteDocument,
  onLogout,
  workspaces,
  defaultUserWorkspaceID,
  defaultUserWorkspaceName,
  onUpdateDefaultWorkspace,
  fetchProjectAndJobs,
  fetchNotes,
  workspaceRole,
}) => {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  const [initialLoading, setInitialLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [focusedRow, setFocusedRow] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchColumn, setSearchColumn] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isClearingFilters, setIsClearingFilters] = useState(false);
  const [isFilterPending, startFilterTransition] = useTransition();
  const [urlNoteId, setUrlNoteId] = useState(
    initialUrlParams?.type === "note" ? initialUrlParams.id : null,
  );
  const [urlTaskId, setUrlTaskId] = useState(
    initialUrlParams?.type === "task" ? initialUrlParams.id : null,
  );
  const [isUrlNoteModalOpen, setIsUrlNoteModalOpen] =
    useState(!!initialUrlParams);
  const [modalItemType, setModalItemType] = useState(
    initialUrlParams?.type || "note",
  );

  // Refs
  const newNoteModalRef = useRef();
  const filterRef = useRef();
  const notesTabRef = useRef();

  const {
    showEditModal,
    setShowEditModal,
    selectedNote,
    setSelectedNote,
    closeEditModal,
    showNewTaskModal,
    setShowNewTaskModal,
    editingTask,
    setEditingTask,
    closeNewTaskModal,
    showNewModal,
    setShowNewModal,
    prefilledData,
    setPrefilledData,
    modalSource,
    setModalSource,
    closeNewModal,
    showAttachedFileModal,
    setShowAttachedFileModal,
    selectedFileNote,
    setSelectedFileNote,
    closeAttachedFileModal,
    showViewModal,
    setShowViewModal,
    viewNote,
    setViewNote,
    closeViewModal,
  } = useModals();

  const {
    stackedState,
    setStackedState,
    setExpandedProjects,
    setExpandedTasks,
    setActiveTabByJob,
    setAssigneeFilters,
  } = useStackedPersistentState();

  const [viewMode, setViewMode] = useLocalStorage("dashboardViewMode", "cards");
  const { currentUser } = useCurrentUser(userid, apiUrl);

  const {
    selectedFilters,
    setSelectedFilters,
    filterOptions,
    filterDropdownOpen,
    setFilterDropdownOpen,
    filterSearchTerm,
    setFilterSearchTerm,
    loadingFilterOptions,
    filterOptionsLoaded,
    setFilterOptionsLoaded,
    fetchFilterOptions,
    handleFilterCheckboxChange,
    removeFilter,
    clearAllFilters,
    getFilterOptions,
    getFilterDisplayValue,
    toggleFilterDropdown,
    hasActiveFilters,
    activeFilterCount,
    filterCombinations,
    generateFilterCombinations,
  } = useFilters(userid, apiUrl);

  const {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    setActiveSearchTerm,
    searchResults,
    searchLoading,
    hasActiveSearchText,
    performSearch,
    handleSearchKeyDown,
    handleClearSearch,
  } = useSearch(userid, apiUrl, selectedFilters, filterCombinations);

  const {
    stackedJobs,
    setStackedJobs,
    filteredStackedJobs,
    setFilteredStackedJobs,
    loadingStackedJobs,
    expandedStacks,
    setExpandedStacks,
    expandedCardLimit,
    notesByTask,
    loadingNotesByTask,
    tasksByJob,
    loadingTasksByJob,
    taskPagination,
    highlightedTaskId,
    setHighlightedTaskId,
    groupFilteredNotesByJob,
    fetchStackedJobs,
    fetchTasksForJob,
    fetchNotesForTask,
    loadMoreTaskNotes,
    toggleStackExpansion: toggleStackExpansionRaw,
    loadMoreCards,
    clearHighlightedTask,
    refreshStackedViewWithNewNote: refreshStackedViewRaw,
  } = useStackedView(
    userid,
    apiUrl,
    viewMode,
    activeSearchTerm,
    hasActiveFilters,
  );

  const {
    filteredNotesFromApi,
    setFilteredNotesFromApi,
    loadingFiltered,
    fetchNotesWithFilters,
    debouncedFetchNotesWithFilters,
  } = useNotesData(
    userid,
    apiUrl,
    generateFilterCombinations,
    activeSearchTerm,
    groupFilteredNotesByJob,
    setFilteredStackedJobs,
  );

  const {
    showPriorityDropdown,
    priorityDropdownPosition,
    selectedNoteForPriority,
    handlePriorityClick,
    handlePriorityChange,
    closePriorityDropdown,
  } = usePriority(
    apiUrl,
    refreshNotes,
    viewMode,
    hasActiveFilters,
    selectedFilters,
    fetchNotesWithFilters,
    fetchStackedJobs,
  );

  const {
    inlineImagesMap,
    loadingImages,
    showImageViewer,
    selectedImage,
    selectedImageNote,
    handleImageThumbnailClick,
    handleNextImage,
    handlePrevImage,
    closeImageViewer,
  } = useInlineImages(apiUrl);

  const {
    defaultWorkspaceRole,
    loadingWorkspaceRole,
    userWorkspace,
    role,
    isRoleLoading,
    fetchWorkspaceRole,
    fetchUserWorkspaces,
  } = useUserWorkspace(userid, defaultUserWorkspaceID, apiUrl);

  const {
    showDeleteConfirm,
    isDeleting,
    handleDelete,
    handleConfirmDelete,
    closeDeleteModal,
  } = useDeleteNote(apiUrl, userid, refreshNotes, {
    onAfterDelete: async (deletedNote) => {
      if (viewMode === "stacked") {
        if (hasActiveFilters) {
          await fetchNotesWithFilters(selectedFilters);
        } else {
          await fetchStackedJobs();
          if (deletedNote.taskId && deletedNote.jobId) {
            await fetchNotesForTask(deletedNote.taskId, deletedNote.jobId);
          }
        }
      }
    },
  });

  const { getFilterButtonLabel, renderActiveFilters } = useActiveFiltersUI({
    selectedFilters,
    loadingFilterOptions,
    filterOptionsLoaded,
    fetchFilterOptions,
    getFilterDisplayValue,
    removeFilter,
    clearAllFilters,
    activeFilterCount,
    workspaces,
    projects,
    jobs,
  });

  const isMobile = useMobileDetection();
  useClickOutside(filterRef, () => {
    setFilterDropdownOpen(null);
    setFilterSearchTerm("");
  });

  // Wrappers for stacked view
  const toggleStackExpansion = useCallback(
    (jobName, jobId) => {
      toggleStackExpansionRaw(
        jobName,
        jobId,
        expandedStacks,
        stackedJobs,
        filteredStackedJobs,
      );
    },
    [toggleStackExpansionRaw, expandedStacks, stackedJobs, filteredStackedJobs],
  );

  const refreshStackedViewWithNewNote = useCallback(
    (noteData, operation = "add") => {
      refreshStackedViewRaw(
        noteData,
        operation,
        fetchNotesWithFilters,
        selectedFilters,
      );
    },
    [refreshStackedViewRaw, fetchNotesWithFilters, selectedFilters],
  );

  // Final notes to display
  const finalDisplayNotes = useMemo(() => {
    if (activeSearchTerm.trim()) return searchResults;
    if (hasActiveFilters) return filteredNotesFromApi;
    return notes || [];
  }, [
    notes,
    hasActiveFilters,
    filteredNotesFromApi,
    searchResults,
    activeSearchTerm,
  ]);

  // Keyboard navigation
  const handleNavigate = useCallback((noteId) => {
    setFocusedRow(noteId);
    setSelectedRow(noteId);
  }, []);

  const handleAddFromSelected = useCallback((note) => {
    const today = new Date();
    const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    setPrefilledData({
      date,
      project: note.project,
      projectId: note.projectId,
      job: note.job,
      jobId: note.jobId,
      workspace: note.workspace,
      taskid: note.taskId,
    });
    setModalSource("grid");
    setShowNewModal(true);
  }, []);

  useKeyboardNavigation(
    focusedRow,
    finalDisplayNotes,
    handleNavigate,
    handleAddFromSelected,
  );

  const handleHighlightNote = useCallback((noteId) => {
    setMobileFiltersOpen(false);
    if (notesTabRef.current?.highlightNote)
      notesTabRef.current.highlightNote(noteId);
  }, []);

  const reloadAllFilters = useCallback(async () => {
    const types = ["date", "workspace", "project", "job", "task", "userName"];
    await Promise.all(types.map((t) => fetchFilterOptions(t)));
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (hasActiveFilters) {
      const filterTypesWithValues = Object.keys(selectedFilters).filter(
        (type) => selectedFilters[type]?.length > 0,
      );
      filterTypesWithValues.forEach((type) => {
        if (!filterOptionsLoaded[type]) {
          fetchFilterOptions(type);
        }
      });
    }
  }, [
    selectedFilters,
    filterOptionsLoaded,
    fetchFilterOptions,
    hasActiveFilters,
  ]);

  const handleFilterByProject = useCallback(
    (projectId, projectName) => {
      if (!projectId) return;
      const newFilters = {
        date: [],
        workspace: [],
        project: [projectId],
        job: [],
        userName: [],
        task: [],
      };
      setSelectedFilters(newFilters);
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") setExpandedStacks({});
      toast.success(`Filtered by project: ${projectName}`);
    },
    [setSelectedFilters, fetchNotesWithFilters, activeSearchTerm, viewMode],
  );

  const handleFilterByWorkspace = useCallback(
    (workspaceId, workspaceName) => {
      if (!workspaceId) return;
      const newFilters = {
        date: [],
        workspace: [workspaceId],
        project: [],
        job: [],
        userName: [],
        task: [],
      };
      setSelectedFilters(newFilters);
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") setExpandedStacks({});
      toast.success(`Filtered by workspace: ${workspaceName}`);
    },
    [setSelectedFilters, fetchNotesWithFilters, activeSearchTerm, viewMode],
  );

  const handleFilterByJob = useCallback(
    (jobId, jobName) => {
      if (!jobId) return;
      const newFilters = {
        date: [],
        workspace: [],
        project: [],
        job: [jobId],
        userName: [],
        task: [],
      };
      setSelectedFilters(newFilters);
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") setExpandedStacks({});
      toast.success(`Filtered by job: ${jobName}`);
    },
    [setSelectedFilters, fetchNotesWithFilters, activeSearchTerm, viewMode],
  );

  const handleFilterByTask = useCallback(
    async (taskId, taskName, job) => {
      if (!taskId || !job) return;
      const newFilters = {
        date: [],
        workspace: [],
        project: [],
        job: [],
        userName: [],
        task: [taskId],
      };
      setSelectedFilters(newFilters);
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") {
        setTimeout(async () => {
          const jobsSource = hasActiveFilters
            ? filteredStackedJobs
            : stackedJobs;
          const targetJob = jobsSource.find((j) => j.jobId === job.jobId);
          if (targetJob) {
            await toggleStackExpansion(targetJob.jobName, targetJob.jobId);
            setTimeout(() => {
              setHighlightedTaskId(taskId);
              window.dispatchEvent(
                new CustomEvent("highlightTask", {
                  detail: { taskId, jobId: job.jobId },
                }),
              );
            }, 500);
          }
        }, 300);
      }
      toast.success(`Filtered by Task: ${taskName}`);
    },
    [
      setSelectedFilters,
      fetchNotesWithFilters,
      activeSearchTerm,
      viewMode,
      hasActiveFilters,
      filteredStackedJobs,
      stackedJobs,
      toggleStackExpansion,
      setHighlightedTaskId,
    ],
  );

  const handleViewAttachments = useCallback((note) => {
    setSelectedFileNote(note);
    setShowAttachedFileModal(true);
  }, []);

  const handleRowDoubleClick = useCallback(
    (note) => {
      let job = jobs.find((j) => j.name && note.job && j.name === note.job);
      if (!job && note.jobId)
        job = jobs.find(
          (j) => (j.id || j.jobId)?.toString() === note.jobId.toString(),
        );
      setViewNote({ id: note.id, jobId: job?.id ?? null });
      setShowViewModal(true);
    },
    [jobs],
  );

  const prevFiltersRef = useRef(null);
  useEffect(() => {
    const previousFilters = prevFiltersRef.current;
    const currentFilters = selectedFilters;
    const changed =
      previousFilters &&
      JSON.stringify(previousFilters) !== JSON.stringify(currentFilters);

    if (
      (!previousFilters && hasActiveFilters) ||
      (changed && hasActiveFilters)
    ) {
      debouncedFetchNotesWithFilters(currentFilters);
    } else if (changed && !hasActiveFilters) {
      setFilteredNotesFromApi([]);
      setFilteredStackedJobs([]);
    }
    prevFiltersRef.current = currentFilters;
  }, [
    hasActiveFilters,
    selectedFilters,
    debouncedFetchNotesWithFilters,
    setFilteredNotesFromApi,
    setFilteredStackedJobs,
  ]);

  useEffect(() => {
    if (userid && defaultUserWorkspaceID) {
      fetchWorkspaceRole();
      fetchUserWorkspaces();
    }
  }, [userid, defaultUserWorkspaceID, fetchWorkspaceRole, fetchUserWorkspaces]);

  useEffect(() => {
    if (notes !== undefined) {
      setIsDataLoaded(true);
      setInitialLoading(false);
    }
  }, [notes]);

  useEffect(() => {
    if (
      viewMode === "stacked" &&
      ((userid && !hasActiveFilters) ||
        activeSearchTerm?.trim() ||
        (hasActiveFilters && activeSearchTerm?.trim()))
    ) {
      fetchStackedJobs();
    }
  }, [
    viewMode,
    userid,
    hasActiveFilters,
    activeSearchTerm,
    selectedFilters,
    fetchStackedJobs,
  ]);

  const handlePriorityUpdate = () => refreshNotes();

  const handleRowClick = useCallback((note) => {
    setFocusedRow(note.id);
    setSelectedRow(note.id);
  }, []);

  const handleRefresh = async () => {
    setInitialLoading(true);
    const prevExpanded = { ...expandedStacks };
    const prevView = viewMode;
    try {
      await refreshNotes();
      setFilterOptionsLoaded({
        date: false,
        workspace: false,
        project: false,
        job: false,
        userName: false,
      });
      await reloadAllFilters();

      if (hasActiveFilters) {
        await fetchNotesWithFilters(selectedFilters);
      }

      if (prevView === "stacked") {
        if (hasActiveFilters) {
          setTimeout(
            () =>
              setFilteredStackedJobs((prev) =>
                prev.map((job) => {
                  if (prevExpanded[job.jobName])
                    setTimeout(
                      () => toggleStackExpansion(job.jobName, job.jobId),
                      100,
                    );
                  return job;
                }),
              ),
            300,
          );
        } else {
          await fetchStackedJobs();
          setTimeout(
            () =>
              setStackedJobs((prev) => {
                Object.keys(prevExpanded).forEach((jobName) => {
                  const idx = prev.findIndex((j) => j.jobName === jobName);
                  if (idx !== -1)
                    setTimeout(
                      () =>
                        toggleStackExpansion(
                          prev[idx].jobName,
                          prev[idx].jobId,
                        ),
                      200,
                    );
                });
                return prev;
              }),
            300,
          );
        }
      }

      const types = ["date", "workspace", "project", "job", "userName", "task"];
      types.forEach((t) => fetchFilterOptions(t));
      toast.success("Refreshed successfully");
    } catch (error) {
      console.error("Refresh error:", error);
      toast.error("Refresh failed");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleAddFromRow = (note) => {
    const today = new Date();
    const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    setPrefilledData({
      date,
      project: note.project,
      projectId: note.projectId,
      job: note.job || note.jobName,
      jobId: note.jobId,
      workspace: note.workspace,
      userId: note.userId,
      taskid: note.taskId || note.id,
      taskName: note.title,
    });
    setModalSource("grid");
    setShowNewModal(true);
  };

  const handleNewNoteClick = () => {
    setPrefilledData(null);
    setModalSource("dashboard");
    setShowNewModal(true);
  };
  const handleNewTaskClick = () => setShowNewTaskModal(true);

  useEffect(() => {
    if (showNewModal && modalSource === "grid" && newNoteModalRef.current) {
      const t = setTimeout(() => newNoteModalRef.current?.focusTextarea(), 500);
      return () => clearTimeout(t);
    }
  }, [showNewModal, modalSource]);

  const handleEdit = (note) => {
    if (note.itemType === "Task") {
      setEditingTask(note);
      setShowNewTaskModal(true);
    } else {
      setSelectedNote(note);
      setShowEditModal(true);
    }
  };

  const handleSearchKeyDownWrapper = useCallback(
    (e) => {
      handleSearchKeyDown(e, () => {
        if (hasActiveFilters) fetchNotesWithFilters(selectedFilters);
        if (viewMode === "stacked") fetchStackedJobs();
      });
    },
    [
      handleSearchKeyDown,
      hasActiveFilters,
      fetchNotesWithFilters,
      selectedFilters,
      viewMode,
      fetchStackedJobs,
    ],
  );

  const renderTableImageIcon = useCallback(
    (note) => (
      <TableImageIcon
        note={note}
        isLoading={loadingImages[note.id]}
        onClick={handleImageThumbnailClick}
      />
    ),
    [loadingImages, handleImageThumbnailClick],
  );
  const renderCardImageIcon = useCallback(
    (note) => (
      <CardImageIcon
        note={note}
        isLoading={loadingImages[note.id]}
        onClick={handleImageThumbnailClick}
      />
    ),
    [loadingImages, handleImageThumbnailClick],
  );
  const renderStackedImageIcon = useCallback(
    (note) => (
      <StackedImageIcon
        note={note}
        isLoading={loadingImages[note.id]}
        onClick={handleImageThumbnailClick}
      />
    ),
    [loadingImages, handleImageThumbnailClick],
  );

  return (
    <div className="main-content">
      <div className="dashboard">
        <MemoizedDashboardMenu
          defaultUserWorkspaceID={defaultUserWorkspaceID}
          defaultUserWorkspaceName={defaultUserWorkspaceName}
          fetchProjectAndJobs={fetchProjectAndJobs}
          onUpdateDefaultWorkspace={onUpdateDefaultWorkspace}
          workspaces={workspaces}
          userid={userid}
          onLogout={onLogout}
          userRole={userRole}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearchKeyDown={handleSearchKeyDownWrapper}
          handleClearSearch={handleClearSearch}
          searchColumn={searchColumn}
          setSearchColumn={setSearchColumn}
          viewMode={viewMode}
          setViewMode={setViewMode}
          handleRefresh={handleRefresh}
          refreshNotes={refreshNotes}
          handleNewTaskModal={handleNewTaskClick}
          defWorkName={defaultUserWorkspaceName}
          role={role}
          onHighlightNote={handleHighlightNote}
        />

        <MemoizedDashboardHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearchKeyDown={handleSearchKeyDownWrapper}
          handleClearSearch={handleClearSearch}
          searchColumn={searchColumn}
          setSearchColumn={setSearchColumn}
          viewMode={viewMode}
          setViewMode={setViewMode}
          handleRefresh={handleRefresh}
          handleNewNoteClick={handleNewNoteClick}
          isMobile={isMobile}
          mobileFiltersOpen={mobileFiltersOpen}
          setMobileFiltersOpen={setMobileFiltersOpen}
          selectedFilters={selectedFilters}
          filterDropdownOpen={filterDropdownOpen}
          toggleFilterDropdown={toggleFilterDropdown}
          filterSearchTerm={filterSearchTerm}
          setFilterSearchTerm={setFilterSearchTerm}
          handleFilterCheckboxChange={handleFilterCheckboxChange}
          getFilterOptions={getFilterOptions}
          getFilterButtonLabel={getFilterButtonLabel}
          clearAllFilters={clearAllFilters}
          getActiveFilterCount={() => activeFilterCount}
          removeFilter={removeFilter}
          getFilterDisplayValue={(type, val) =>
            getFilterDisplayValue(type, val, workspaces, projects, jobs)
          }
          filterRef={filterRef}
          loadingFilterOptions={loadingFilterOptions}
          renderActiveFilters={renderActiveFilters}
          userId={userid}
          onFilterByTask={handleFilterByTask}
          toggleStackExpansion={toggleStackExpansion}
          handleFilterByProject={handleFilterByProject}
          handleFilterByWorkspace={handleFilterByWorkspace}
          handleFilterByJob={handleFilterByJob}
        />

        <MemoizedNotesTab
          viewMode={viewMode}
          finalDisplayNotes={finalDisplayNotes}
          isDataLoaded={isDataLoaded}
          initialLoading={initialLoading}
          searchLoading={searchLoading}
          loadingFiltered={loadingFiltered}
          searchTerm={activeSearchTerm}
          getActiveFilterCount={() => activeFilterCount}
          handleRowClick={handleRowClick}
          handleRowDoubleClick={handleRowDoubleClick}
          handleAddFromRow={handleAddFromRow}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleViewAttachments={handleViewAttachments}
          selectedRow={selectedRow}
          focusedRow={focusedRow}
          inlineImagesMap={inlineImagesMap}
          loadingImages={loadingImages}
          renderTableImageIcon={renderTableImageIcon}
          renderCardImageIcon={renderCardImageIcon}
          renderStackedImageIcon={renderStackedImageIcon}
          handleImageThumbnailClick={handleImageThumbnailClick}
          handlePriorityClick={handlePriorityClick}
          handlePriorityChange={handlePriorityChange}
          showPriorityDropdown={showPriorityDropdown}
          setShowPriorityDropdown={closePriorityDropdown}
          selectedNoteForPriority={selectedNoteForPriority}
          priorityDropdownPosition={priorityDropdownPosition}
          expandedStacks={expandedStacks}
          toggleStackExpansion={toggleStackExpansion}
          expandedCardLimit={expandedCardLimit}
          loadMoreCards={loadMoreCards}
          jobs={jobs}
          tasks={tasks}
          setViewNote={setViewNote}
          setShowViewModal={setShowViewModal}
          stackedJobs={hasActiveFilters ? filteredStackedJobs : stackedJobs}
          loadingStackedJobs={loadingStackedJobs}
          fetchStackedJobs={fetchStackedJobs}
          fetchNotes={fetchNotes}
          userId={userid}
          hasActiveFilters={hasActiveFilters}
          filteredNotesFromApi={filteredNotesFromApi}
          searchResults={searchResults}
          handleFilterCheckboxChange={handleFilterCheckboxChange}
          removeFilter={removeFilter}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterCheckboxChange}
          projects={projects}
          workspaces={workspaces}
          fetchNotesForTask={fetchNotesForTask}
          loadingNotesByTask={loadingNotesByTask}
          notesByTask={notesByTask}
          fetchTasksForJob={fetchTasksForJob}
          tasksByJob={tasksByJob}
          loadingTasksByJob={loadingTasksByJob}
          isClearingFilter={isClearingFilters}
          onFilterByProject={handleFilterByProject}
          refreshNotes={refreshNotes}
          onFilterByWorkspace={handleFilterByWorkspace}
          highlightedTaskId={highlightedTaskId}
          clearHighlightedTask={clearHighlightedTask}
          onFilterByTask={handleFilterByTask}
          loadMoreTaskNotes={loadMoreTaskNotes}
          taskPagination={taskPagination}
          ref={notesTabRef}
          workspaceRole={workspaceRole}
          stackedState={stackedState}
          setExpandedProjects={setExpandedProjects}
          setExpandedTasks={setExpandedTasks}
          setActiveTabByJob={setActiveTabByJob}
          setAssigneeFilters={setAssigneeFilters}
        />
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      {showViewModal && viewNote && (
        <ViewNoteModal
          noteId={viewNote.id}
          jobId={viewNote.jobId}
          workspace={viewNote.workspace}
          job={viewNote.job}
          project={viewNote.project}
          onClose={closeViewModal}
          onViewAttachments={handleViewAttachments}
          currentTheme="gray"
          userid={userid}
          scrollToNoteId={viewNote.id}
        />
      )}
      {showNewModal && (
        <NewNoteModal
          ref={newNoteModalRef}
          isOpen={showNewModal}
          onClose={closeNewModal}
          refreshNotes={refreshNotes}
          refreshFilteredNotes={() => {}}
          addSiteNote={addSiteNote}
          onUploadDocument={onUploadDocument}
          onDeleteDocument={onDeleteDocument}
          defWorkSpaceId={defaultUserWorkspaceID}
          userworksaces={userWorkspace}
          prefilledData={prefilledData}
          source={modalSource}
          defaultWorkspaceRole={defaultWorkspaceRole}
          fetchNotesWithFilters={fetchNotesWithFilters}
          selectedFilters={selectedFilters}
          hasActiveFilters={hasActiveFilters}
          viewMode={viewMode}
          refreshStackedView={refreshStackedViewWithNewNote}
          hasActiveSearchText={hasActiveSearchText}
          performSearching={performSearch}
          searchTerm={searchTerm}
          fetchNotesForTask={fetchNotesForTask}
          fetchTasksForJob={fetchTasksForJob}
        />
      )}
      {showEditModal && selectedNote && (
        <EditNoteModal
          note={selectedNote}
          onClose={closeEditModal}
          refreshNotes={refreshNotes}
          updateNote={updateNote}
          deleteDocument={onDeleteDocument}
          uploadDocument={onUploadDocument}
          projects={projects}
          jobs={jobs}
          defaultUserWorkspaceID={defaultUserWorkspaceID}
          onPriorityUpdate={handlePriorityUpdate}
          openToPriorityTab={true}
          fetchNotesWithFilters={fetchNotesWithFilters}
          selectedFilters={selectedFilters}
          hasActiveFilters={hasActiveFilters}
          viewMode={viewMode}
          refreshStackedView={refreshStackedViewWithNewNote}
          fetchNotesForTask={fetchNotesForTask}
        />
      )}
      {showAttachedFileModal && selectedFileNote && (
        <AttachedFileModal
          note={selectedFileNote}
          onClose={closeAttachedFileModal}
          refreshNotes={refreshNotes}
          updateNote={updateNote}
          projects={projects}
          jobs={jobs}
          uploadDocument={onUploadDocument}
          onDeleteDocument={onDeleteDocument}
        />
      )}
      {showImageViewer && selectedImage && selectedImageNote && (
        <InlineImageViewer
          image={selectedImage.image}
          note={selectedImageNote}
          currentIndex={selectedImage.index}
          totalImages={selectedImage.total}
          onClose={closeImageViewer}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
          apiUrl={apiUrl}
        />
      )}
      {showNewTaskModal && (
        <NewTaskModal
          isOpen={showNewTaskModal}
          onClose={closeNewTaskModal}
          defaultWorkspaceRole={defaultWorkspaceRole}
          userworksaces={userWorkspace}
          refreshNotes={refreshNotes}
          isEditMode={true}
          editTaskData={editingTask}
        />
      )}
      <UrlNoteModal
        isOpen={isUrlNoteModalOpen}
        noteId={urlNoteId}
        taskId={urlTaskId}
        itemType={modalItemType}
        onClose={() => {
          setIsUrlNoteModalOpen(false);
          setUrlNoteId(null);
          setUrlTaskId(null);
          setModalItemType("note");
        }}
        apiUrl={apiUrl}
        refreshNotes={refreshNotes}
        userId={userid}
        updateNote={updateNote}
      />
    </div>
  );
};

Dashboard.propTypes = {
  notes: PropTypes.array.isRequired,
  refreshNotes: PropTypes.func.isRequired,
  addSiteNote: PropTypes.func.isRequired,
  updateNote: PropTypes.func.isRequired,
  projects: PropTypes.array,
  jobs: PropTypes.array,
  tasks: PropTypes.array,
  onUploadDocument: PropTypes.func.isRequired,
  onDeleteDocument: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  workspaces: PropTypes.array,
  defaultUserWorkspaceID: PropTypes.any,
  defaultUserWorkspaceName: PropTypes.string,
  onUpdateDefaultWorkspace: PropTypes.func.isRequired,
  fetchProjectAndJobs: PropTypes.func.isRequired,
};

Dashboard.defaultProps = {
  projects: [],
  jobs: [],
  tasks: [],
  workspaces: [],
  fetchNotes: () => {},
};

export default Dashboard;
